import { useEffect, useState, type ReactNode } from 'react';
import type { PublicStream } from '@zip2live/shared';
import { DELETE_ACCOUNT_CONFIRM, removalLabel, type AppealState, type SafetyStatus } from '@zip2live/shared/safety';
import { Link } from './Brand.tsx';
import { appeal, block, deleteAccount, unblock, useBlocks } from '../lib/safety.ts';
import { navigate } from '../lib/router.ts';

/** Bottom sheet on phones, centred card on wider screens. */
export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div
        className="max-h-[92dvh] w-full max-w-md overflow-y-auto rounded-t-[28px] bg-white p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-navy sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="speed-text text-2xl">{title}</p>
        {children}
      </div>
    </div>
  );
}

const field = 'mt-1 w-full rounded-xl border-2 border-line bg-white px-3.5 py-2.5 text-[16px] outline-none focus:border-navy';
const label = 'mt-4 block text-sm font-bold';

// ---- Blocking ----

/** "Block @creator" under the video, with a confirmation. Hidden from the creator themself. */
export function BlockCreatorLink({ stream, name, className = 'mt-2 block text-xs text-faint-dark' }: { stream: PublicStream; name: string; className?: string }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await block(stream.id);
      setOpen(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" className={`${className} underline underline-offset-2`} onClick={() => setOpen(true)}>
        Block {name}
      </button>
      {open && (
        <Sheet title={`Block ${name}?`} onClose={() => setOpen(false)}>
          <p className="mt-1 text-[15px] text-muted">
            You won’t see their streams on Live Now or their messages in chat, and they can’t chat on your streams. They aren’t told. You can unblock them on your account page.
          </p>
          {error && <p role="alert" className="mt-3 font-bold text-race">{error}</p>}
          <button type="button" disabled={busy} className="btn-race mt-5 h-12 w-full text-lg" onClick={() => void go()}>
            {busy ? 'BLOCKING…' : 'BLOCK'}
          </button>
          <button type="button" className="mt-3 h-11 w-full font-bold text-muted underline underline-offset-2" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </Sheet>
      )}
    </>
  );
}

/** Shown instead of the video when you blocked the creator. */
export function BlockedCreator({ streamId, name, onWatch }: { streamId: string; name: string; onWatch: () => void }) {
  const { creatorKey } = useBlocks(streamId);
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex min-h-[14rem] w-full flex-col items-center justify-center bg-navy px-6 py-8 text-center sm:aspect-video sm:rounded-2xl">
      <p className="speed-text text-2xl sm:text-4xl">You blocked {name}</p>
      <p className="mt-2 max-w-md text-sm text-muted-dark sm:text-base">Their streams and chat messages are hidden for you.</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <button type="button" onClick={onWatch} className="h-11 rounded-xl bg-white px-5 font-extrabold text-navy hover:bg-mist">
          Watch anyway
        </button>
        <button
          type="button"
          disabled={busy || !creatorKey}
          onClick={() => {
            setBusy(true);
            void unblock(creatorKey!, streamId).finally(() => setBusy(false));
          }}
          className="h-11 rounded-xl border-2 border-line-dark px-5 font-extrabold text-white hover:bg-white/10 disabled:opacity-50"
        >
          Unblock
        </button>
      </div>
    </div>
  );
}

/** People you blocked, with Unblock. Hidden when there are none. */
export function BlockedList() {
  const { blocks, loaded } = useBlocks();
  if (!loaded || !blocks.length) return null;
  return (
    <>
      <h2 className="speed-text mt-12 text-[28px]">Blocked</h2>
      <p className="mt-2 max-w-2xl text-muted">You don’t see their streams or chat messages, and they can’t chat on your streams.</p>
      <ul className="mt-4 divide-y divide-line rounded-3xl border border-line bg-white">
        {blocks.map((b) => (
          <li key={b.key} className="flex items-center justify-between gap-4 px-5 py-3.5">
            <div className="min-w-0">
              <p className="truncate font-bold">{b.name}</p>
              <p className="text-[13px] text-muted">Blocked {new Date(b.createdAt).toLocaleDateString()}</p>
            </div>
            <button type="button" className="h-10 shrink-0 rounded-lg border-2 border-line px-4 text-sm font-extrabold hover:bg-mist" onClick={() => void unblock(b.key)}>
              Unblock
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

// ---- Removals, suspension and appeals ----

const APPEAL_LABEL: Record<AppealState['status'], string> = {
  pending: 'Appeal sent. We’ll look at it again.',
  accepted: 'Appeal accepted.',
  denied: 'Appeal denied.',
};

function AppealLine({ state }: { state: AppealState }) {
  return (
    <span className={state.status === 'denied' ? 'text-race' : ''}>
      {APPEAL_LABEL[state.status]}
      {state.note && <span className="text-muted"> “{state.note}”</span>}
    </span>
  );
}

/** Under a removed stream in Your streams: why, and the appeal. */
export function RemovalNote({ removal, onAppealed }: { removal: SafetyStatus['removals'][number]; onAppealed: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <span className="font-bold text-race">
        Removed: {removal.reason === 'copyright' ? 'after a copyright notice' : removalLabel(removal.reason).toLowerCase()}
      </span>
      {removal.appeal ? (
        <AppealLine state={removal.appeal} />
      ) : (
        <button type="button" className="font-bold text-navy underline underline-offset-2" onClick={() => setOpen(true)}>
          {removal.reason === 'copyright' ? 'Send a counter-notice' : 'Appeal'}
        </button>
      )}
      {open && <AppealSheet streamId={removal.streamId} copyright={removal.reason === 'copyright'} onClose={() => setOpen(false)} onDone={onAppealed} />}
    </span>
  );
}

export function SuspensionBanner({ suspension, onAppealed }: { suspension: NonNullable<SafetyStatus['suspension']>; onAppealed: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <section role="alert" className="mt-7 rounded-3xl border-2 border-race bg-white p-6">
      <p className="speed-text text-2xl text-race">Your account is suspended</p>
      <p className="mt-2 text-muted">
        You can’t go live or chat. {suspension.reason} (since {new Date(suspension.since).toLocaleDateString()})
      </p>
      <p className="mt-3 text-sm">
        {suspension.appeal ? (
          <AppealLine state={suspension.appeal} />
        ) : (
          <button type="button" className="h-11 rounded-xl bg-navy px-5 font-extrabold text-white hover:bg-navy-700" onClick={() => setOpen(true)}>
            Appeal
          </button>
        )}
      </p>
      {open && <AppealSheet copyright={false} onClose={() => setOpen(false)} onDone={onAppealed} />}
    </section>
  );
}

/**
 * Ask for a removal (or the suspension, without a stream) to be looked at again. A copyright removal
 * takes a counter-notice: the details US copyright law asks for, which we pass to the person who
 * complained.
 */
function AppealSheet({ streamId, copyright, onClose, onDone }: { streamId?: string; copyright: boolean; onClose: () => void; onDone: () => void }) {
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [mistake, setMistake] = useState(false);
  const [jurisdiction, setJurisdiction] = useState(false);
  const [signature, setSignature] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ready = message.trim().length >= 10 && (!copyright || (name.trim().length >= 2 && address.trim().length >= 5 && mistake && jurisdiction && signature.trim().length >= 2));
  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await appeal({
        streamId,
        message: message.trim(),
        counterNotice: copyright ? { name: name.trim(), address: address.trim(), phone: phone.trim() || undefined, mistake: true, jurisdiction: true, signature: signature.trim() } : undefined,
      });
      onDone();
      onClose();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };
  return (
    <Sheet title={copyright ? 'Copyright counter-notice' : 'Appeal'} onClose={onClose}>
      <form onSubmit={(e) => void send(e)}>
        <p className="mt-1 text-[15px] text-muted">
          {copyright
            ? 'If you own the content or have the right to use it, tell us why. We send this counter-notice, with your name and address, to the person who complained. Unless they go to court, we can put the stream back after 10 business days.'
            : streamId
              ? 'Tell us why this stream should be put back. A person on our team reviews every appeal.'
              : 'Tell us why your account should be unsuspended. A person on our team reviews every appeal.'}
        </p>
        <label className={label}>
          Why {copyright ? 'it was removed by mistake' : 'we got it wrong'}
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={2000} className={field} />
        </label>
        {copyright && (
          <>
            <label className={label}>
              Full legal name
              <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" className={field} />
            </label>
            <label className={label}>
              Postal address
              <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} autoComplete="street-address" className={field} />
            </label>
            <label className={label}>
              Phone (optional)
              <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" autoComplete="tel" className={field} />
            </label>
            <label className="mt-4 flex gap-3 text-sm">
              <input type="checkbox" checked={mistake} onChange={(e) => setMistake(e.target.checked)} className="mt-1 h-4 w-4 shrink-0" />
              I swear, under penalty of perjury, that I have a good-faith belief the material was removed or disabled as a result of a mistake or misidentification.
            </label>
            <label className="mt-3 flex gap-3 text-sm">
              <input type="checkbox" checked={jurisdiction} onChange={(e) => setJurisdiction(e.target.checked)} className="mt-1 h-4 w-4 shrink-0" />
              I consent to the jurisdiction of the federal district court for my address (or, outside the US, any district where ZIP2LIVE may be found), and I will accept service of process from the person who sent the notice or their agent.
            </label>
            <label className={label}>
              Signature (type your full name)
              <input value={signature} onChange={(e) => setSignature(e.target.value)} className={field} />
            </label>
          </>
        )}
        {error && <p role="alert" className="mt-3 font-bold text-race">{error}</p>}
        <button type="submit" disabled={busy || !ready} className="btn-race mt-5 h-12 w-full text-lg disabled:opacity-50">
          {busy ? 'SENDING…' : 'SEND'}
        </button>
        <button type="button" className="mt-3 h-11 w-full font-bold text-muted underline underline-offset-2" onClick={onClose}>
          Cancel
        </button>
      </form>
    </Sheet>
  );
}

// ---- Delete account ----

export function DeleteAccount({ guest }: { guest: boolean }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const go = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await deleteAccount();
      navigate('/account-deleted');
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };
  return (
    <section className="mt-14 border-t border-line pt-8">
      <h2 className="speed-text text-xl">Delete {guest ? 'your guest data' : 'your account'}</h2>
      <p className="mt-2 max-w-2xl text-sm text-muted">
        Deletes your streams and replays, your chat name and messages, your mods, bans and blocks{guest ? '' : ', your link and your sign-in details'}. It happens straight away and can’t be undone.
      </p>
      <button type="button" className="mt-4 h-11 rounded-xl border-2 border-race px-5 text-sm font-extrabold text-race hover:bg-race hover:text-white" onClick={() => setOpen(true)}>
        Delete {guest ? 'guest data' : 'account'}
      </button>
      {open && (
        <Sheet title={guest ? 'Delete your guest data?' : 'Delete your account?'} onClose={() => setOpen(false)}>
          <form onSubmit={(e) => void go(e)}>
            <p className="mt-1 text-[15px] text-muted">
              Every stream and replay you made is deleted for everyone and their links stop working{guest ? '' : ', and your zip2live.com link is retired for good, so nobody else can ever claim it'}. You’ll be signed out. This can’t be undone.
            </p>
            <label className={label}>
              Type {DELETE_ACCOUNT_CONFIRM} to confirm
              <input value={typed} onChange={(e) => setTyped(e.target.value)} autoCapitalize="characters" autoCorrect="off" autoComplete="off" className={field} />
            </label>
            {error && <p role="alert" className="mt-3 font-bold text-race">{error}</p>}
            <button type="submit" disabled={busy || typed.trim() !== DELETE_ACCOUNT_CONFIRM} className="btn-race mt-5 h-12 w-full text-lg disabled:opacity-50">
              {busy ? 'DELETING…' : guest ? 'DELETE GUEST DATA' : 'DELETE ACCOUNT'}
            </button>
            <button type="button" className="mt-3 h-11 w-full font-bold text-muted underline underline-offset-2" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </form>
        </Sheet>
      )}
    </section>
  );
}

/** Staff only: a way into the review queue from the account page. */
export function ReviewQueueLink() {
  return (
    <Link to="/admin" className="mt-6 inline-flex h-11 items-center rounded-xl bg-navy px-5 font-extrabold text-white hover:bg-navy-700">
      Review queue
    </Link>
  );
}
